import React from 'react';
import { observer, inject } from 'mobx-react';
import qs from 'qs';
import Pagination from 'components/Pagination';
import Footer from 'components/Footer';
import { formatTimeOnly, formatDateOnly } from 'helpers/time';
import { currencyFormattedAmount } from 'helpers/currency';
import { QUOTE_COLUMNS, QUOTE_MIN_TABLE_WIDTH } from 'components/Tables/Quotes';
import * as DataList from 'components/DataList';

// A client's estimates (client page, Estimates tab), laid out like the
// Estimates list (components/Tables/Quotes), with the customer column showing
// the estimate's location (every row is this client).
const LOCATION_COLUMN = { area: 'where', label: 'Location', width: 'minmax(180px, 1.3fr)', icon: 'map-marker' };

const COLUMNS = QUOTE_COLUMNS.map((column) => (column.area === 'client' ? LOCATION_COLUMN : column));

const CARDS = {
  columns: 'minmax(0, 1fr) auto auto',
  areas: [
    'estimate status actions',
    'description description description',
    'job job job',
    'where where where',
    'created_by total total',
  ],
  footer: ['created_by', 'total'],
};

const MIN_TABLE_WIDTH = QUOTE_MIN_TABLE_WIDTH;

// Estimate status -> pill tone (same as the Estimates list).
const STATUS_TONES = {
  draft: 'grey',
  submitted: 'blue',
  sent: 'blue',
  approved: 'green',
  on_hold: 'amber',
  rejected: 'red',
};

// "on_hold" -> "On hold"
const sentenceCase = (value) => {
  const text = String(value || '').replace(/_/g, ' ');
  return text.charAt(0).toUpperCase() + text.slice(1);
};

// Descriptions are stored as HTML; the list shows plain text.
const htmlToText = (html) => {
  if (!html) return '';
  const node = document.createElement('div');
  node.innerHTML = html;
  return (node.textContent || '').replace(/\s+/g, ' ').trim();
};

@inject('viewStore', 'quoteStore') @observer
class QuotesTable extends React.Component {

  componentDidMount() {
    this.props.viewStore.clientQuoteSearch.where.client_id = this.props.client.attributes.id;
    this.props.quoteStore.loadClientQuotes();
  }

  onPaginationChange(newPage) {
    const { viewStore } = this.props;
    viewStore.clientQuoteSearch = {
      ...viewStore.clientQuoteSearch,
      page: newPage,
    };
    this.props.quoteStore.loadClientQuotes();
  }

  render() {
    const {
      viewStore,
      viewStore: {
        jobTab: { section },
      },
      quoteStore: {
        clientQuotes: { objects, isLoading, isError, meta },
      },
    } = this.props;

    if (isLoading && objects.length === 0) {
      return (
        <DataList.Loading columns={COLUMNS} cards={CARDS} minTableWidth={MIN_TABLE_WIDTH} noun="estimates" rows={5} />
      );
    }
    if (isError) {
      return (
        <DataList.LoadError
          noun="estimates"
          onRetry={() => this.onPaginationChange((meta && meta.page) || 1)}
        />
      );
    }
    if (objects.length === 0) {
      return (
        <DataList.Empty
          noun="estimates"
          message="Estimates you create for this client will show up here."
        />
      );
    }

    const pagination = meta && meta.total_pages > 1 && (
      <Footer>
        <div className="bp3-navbar-group bp3-align-left">
          <Pagination value={meta.page} total={meta.total_pages} onChange={this.onPaginationChange.bind(this)} />
        </div>
      </Footer>
    );

    return (
      <React.Fragment>
        <DataList.List
          preferTable
          columns={COLUMNS}
          cards={CARDS}
          minTableWidth={MIN_TABLE_WIDTH}
          total={(meta && meta.total_records) || objects.length}
          noun="estimates"
          note={DataList.sortNote(viewStore.clientQuoteSearch.order)}
        >
          {objects.map((quote) => {
            const attributes = quote.attributes;
            const job = attributes.job || {};
            const createdBy = attributes.created_by || {};
            const description = htmlToText(attributes.description);
            const showLink = `/dashboard/quotes/${quote.id}/show`;
            const jobLink = job.id ? `/dashboard/jobs/${job.id}/${section}` : null;
            const jobNumber = job.event_number || attributes.event_number;
            return (
              <DataList.Row key={quote.id}>
                <DataList.Cell area="estimate">
                  <div className="dl-lines">
                    <DataList.RowLink href={showLink} label={`Estimate ${attributes.number}`}>
                      #{attributes.number}
                    </DataList.RowLink>
                    <div className="dl-muted dl-truncate">
                      {formatDateOnly(attributes.created_at)}, {formatTimeOnly(attributes.created_at)}
                    </div>
                  </div>
                </DataList.Cell>
                <DataList.Cell area="job">
                  {jobNumber ? (
                    <DataList.Lines
                      primary={<React.Fragment><DataList.CardOnly>Job </DataList.CardOnly>#{jobNumber}</React.Fragment>}
                      title={`Job #${jobNumber}`}
                      href={jobLink || undefined}
                      secondary={job.service_type_name}
                    />
                  ) : (
                    <DataList.Muted>No job</DataList.Muted>
                  )}
                </DataList.Cell>
                <DataList.Cell area="where">
                  {attributes.location && attributes.location.full_address ? (
                    <DataList.Lines primary={attributes.location.full_address} title={attributes.location.full_address} strong={false} />
                  ) : (
                    <DataList.Muted>No location</DataList.Muted>
                  )}
                </DataList.Cell>
                <DataList.Cell area="description">
                  <DataList.Lines
                    primary={description || <DataList.Muted>No description</DataList.Muted>}
                    title={description || undefined}
                    strong={false}
                    secondary={attributes.po_number ? `PO ${attributes.po_number}` : null}
                  />
                </DataList.Cell>
                <DataList.Cell area="created_by">
                  {createdBy.full_name ? (
                    <DataList.Lines
                      primary={<React.Fragment><DataList.CardOnly>Created by </DataList.CardOnly>{createdBy.full_name}</React.Fragment>}
                      title={createdBy.full_name}
                      href={createdBy.id
                        ? `/dashboard/users/${createdBy.id}/show?${qs.stringify(viewStore.userShowSearch, { encode: false })}`
                        : undefined}
                      secondary={createdBy.email}
                    />
                  ) : (
                    <DataList.Muted>Unknown</DataList.Muted>
                  )}
                </DataList.Cell>
                <DataList.Cell area="total">
                  <DataList.Lines primary={currencyFormattedAmount(attributes.grand_total)} />
                </DataList.Cell>
                <DataList.Cell area="status">
                  {attributes.status && (
                    <DataList.Pill tone={STATUS_TONES[attributes.status] || 'grey'}>
                      {sentenceCase(attributes.status)}
                    </DataList.Pill>
                  )}
                </DataList.Cell>
                <DataList.Cell area="actions">
                  <DataList.Actions
                    items={[
                      jobLink && { icon: 'eye-open', label: 'View job', href: jobLink },
                      { icon: 'edit', label: 'View or edit estimate', href: showLink },
                    ]}
                  />
                </DataList.Cell>
              </DataList.Row>
            );
          })}
        </DataList.List>
        {pagination}
      </React.Fragment>
    );
  }
}

export default QuotesTable;
